import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import * as api from "../services/api";
import {
  defaultState,
  parseIntoV2,
  loadAndMigrate,
  buildDailySummaries,
  computeDayFlow,
  computeDebtStrategy,
  mergeById,
  mergeOB,
  SCHEMA_VERSION,
} from "../utils/data";
import { todayStr, dispDate, nextDay } from "../utils/dates";
import { fmt } from "../utils/format";

export const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [data, setData] = useState(defaultState);
  const [form, setForm] = useState({});
  const TODAY = useMemo(() => todayStr(), []);
  const [toast, setToast] = useState(null);
  const [filterDate, setFilterDate] = useState("");
  const [debtStrategy, setDebtStrategy] = useState("suggested");
  const [availableCash, setAvailableCash] = useState("");
  const [partialPayId, setPartialPayId] = useState(null);
  const [partialAmt, setPartialAmt] = useState("");
  const [dailyViewDate, setDailyViewDate] = useState(TODAY);
  const [importStatus, setImportStatus] = useState(null); // { type, msg }

  // ─── Auth state ─────────────────────────────────────────────────────────────
  const [authUser, setAuthUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authMode, setAuthMode] = useState("login"); // "login" | "register"
  const [authForm, setAuthForm] = useState({ name: "", email: "", password: "" });
  const [authError, setAuthError] = useState("");
  const [authSubmitting, setAuthSubmitting] = useState(false);

  // On mount: if a token exists load all data from API, otherwise show auth screen
  useEffect(() => {
    const token = api.getToken();
    if (!token) {
      setAuthLoading(false);
      return;
    }

    api.data
      .getAll()
      .then((loaded) => {
        setData({ ...defaultState, ...parseIntoV2(loaded) });
        return api.auth.me();
      })
      .then((res) => {
        if (res?.user) setAuthUser(res.user);
      })
      .catch(() => {
        const migrated = loadAndMigrate();
        setData({ ...defaultState, ...migrated });
        api.setToken(null);
      })
      .finally(() => setAuthLoading(false));
  }, []);

  // save() updates React state only; API calls happen inside each handler
  const save = useCallback((next) => {
    setData(next);
  }, []);

  // ─── Auth handlers ───────────────────────────────────────────────────────────
  async function handleAuthSubmit(e) {
    e.preventDefault();
    setAuthError("");

    // Client-side validation
    if (authMode === "register") {
      if (!authForm.name.trim()) {
        setAuthError("Full name is required.");
        return;
      }
      if (!authForm.email.trim()) {
        setAuthError("Email address is required.");
        return;
      }
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(authForm.email.trim());
      if (!emailOk) {
        setAuthError("Please enter a valid email address.");
        return;
      }
      if (!authForm.password) {
        setAuthError("Password is required.");
        return;
      }
      if (authForm.password.length < 6) {
        setAuthError("Password must be at least 6 characters.");
        return;
      }
    } else {
      if (!authForm.email.trim()) {
        setAuthError("Email address is required.");
        return;
      }
      if (!authForm.password) {
        setAuthError("Password is required.");
        return;
      }
    }

    setAuthSubmitting(true);
    try {
      let res;
      if (authMode === "register") {
        res = await api.auth.register(authForm.name, authForm.email, authForm.password);
      } else {
        res = await api.auth.login(authForm.email, authForm.password);
      }
      setAuthUser(res.user);
      const loaded = await api.data.getAll();
      setData({ ...defaultState, ...parseIntoV2(loaded) });
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthSubmitting(false);
    }
  }

  function handleLogout() {
    api.auth.logout();
    setAuthUser(null);
    setData(defaultState);
    setAuthForm({ name: "", email: "", password: "" });
  }

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  // ─── Computed ───────────────────────────────────────────────────────────────
  const dailySummaries = useMemo(() => buildDailySummaries(data), [data]);

  const todaySummary = useMemo(() => {
    const found = dailySummaries.find((s) => s.date === TODAY);
    if (found) return found;
    const last = dailySummaries[dailySummaries.length - 1];
    return computeDayFlow(TODAY, data, last ? last.eodBalance : 0);
  }, [dailySummaries, TODAY, data]);

  const viewedDaySummary = useMemo(() => {
    const found = dailySummaries.find((s) => s.date === dailyViewDate);
    if (found) return found;
    const idx = dailySummaries.findIndex((s) => s.date > dailyViewDate);
    const prev =
      idx > 0
        ? dailySummaries[idx - 1]
        : dailySummaries[dailySummaries.length - 1];
    return computeDayFlow(dailyViewDate, data, prev ? prev.eodBalance : 0);
  }, [dailySummaries, dailyViewDate, data]);

  const pendingVendor = data.purchases.filter((p) => !p.paid && p.payDue <= TODAY);
  const debts = data.debts || [];
  const totalDebt = debts.reduce((a, d) => a + d.remaining, 0);
  const overdueDebts = debts.filter(
    (d) => d.dueDate && d.dueDate < TODAY && d.remaining > 0,
  );
  const debtResult = computeDebtStrategy(debts, parseFloat(availableCash) || 0);
  const strategyList =
    debtStrategy === "avalanche"
      ? debtResult.avalanche
      : debtStrategy === "snowball"
        ? debtResult.snowball
        : debtResult.suggested;

  const expByCategory = useMemo(
    () =>
      (data.expenses || []).reduce((acc, e) => {
        acc[e.category] = (acc[e.category] || 0) + e.amount;
        return acc;
      }, {}),
    [data.expenses],
  );
  const totalExpenses = (data.expenses || []).reduce((a, b) => a + b.amount, 0);

  // All-time totals
  const allTimeSalesTotal = data.sales.reduce((a, b) => a + b.amount, 0);
  const allTimeVendorPaid = data.purchases
    .filter((p) => p.paid)
    .reduce((a, b) => a + b.amount, 0);
  const allTimeVendorUnpaid = data.purchases
    .filter((p) => !p.paid)
    .reduce((a, b) => a + b.amount, 0);
  const allTimeSalaries = data.salaries.reduce((a, b) => a + b.amount, 0);

  // ─── Handlers ────────────────────────────────────────────────────────────────
  async function setOpeningBalance() {
    if (!form.obAmount || !form.obDate) {
      showToast("Enter date and amount", "error");
      return;
    }
    const entry = {
      date: form.obDate,
      amount: parseFloat(form.obAmount),
      note: form.obNote || "",
    };
    try {
      await api.openingBalances.upsert(entry.date, entry.amount, entry.note);
      const existing = (data.openingBalances || []).filter(
        (b) => b.date !== form.obDate,
      );
      save({
        ...data,
        openingBalances: [...existing, entry].sort((a, b) =>
          a.date.localeCompare(b.date),
        ),
      });
      setForm({});
      showToast(`Opening balance set to ${fmt(entry.amount)} for ${dispDate(entry.date)}`);
    } catch (err) {
      showToast(err.message, "error");
    }
  }

  async function addPurchase() {
    if (!form.vendor || !form.amount) {
      showToast("Fill vendor name and amount", "error");
      return;
    }
    const purchaseDate = form.purchaseDate || TODAY;
    const entry = {
      id: Date.now(),
      vendor: form.vendor,
      item: form.item || "",
      amount: parseFloat(form.amount),
      purchaseDate,
      payDue: nextDay(purchaseDate),
      paid: false,
    };
    try {
      const saved = await api.purchases.create(entry);
      save({ ...data, purchases: [saved, ...data.purchases] });
      setForm({});
      showToast("Purchase recorded — payment due tomorrow!");
    } catch (err) {
      showToast(err.message, "error");
    }
  }

  async function markVendorPaid(id) {
    try {
      const updated = await api.purchases.markPaid(id, TODAY);
      save({
        ...data,
        purchases: data.purchases.map((p) => (p.id === id ? updated : p)),
      });
      showToast("Vendor payment marked as paid ✓");
    } catch (err) {
      showToast(err.message, "error");
    }
  }

  async function addExpense() {
    if (!form.expDesc || !form.expAmount) {
      showToast("Fill description and amount", "error");
      return;
    }
    const entry = {
      id: Date.now(),
      description: form.expDesc,
      category: form.expCat || "Miscellaneous",
      amount: parseFloat(form.expAmount),
      date: form.expDate || TODAY,
      vendor: form.expVendor || "",
    };
    try {
      const saved = await api.expenses.create(entry);
      save({ ...data, expenses: [saved, ...(data.expenses || [])] });
      setForm({});
      showToast("Expense recorded!");
    } catch (err) {
      showToast(err.message, "error");
    }
  }

  async function addSale() {
    if (!form.client || !form.amount) {
      showToast("Fill client name and amount", "error");
      return;
    }
    const entry = {
      id: Date.now(),
      client: form.client,
      description: form.desc || "",
      amount: parseFloat(form.amount),
      date: form.saleDate || TODAY,
    };
    try {
      const saved = await api.sales.create(entry);
      save({ ...data, sales: [saved, ...data.sales] });
      setForm({});
      showToast("Sale recorded — cash received!");
    } catch (err) {
      showToast(err.message, "error");
    }
  }

  async function addSalary() {
    if (!form.employee || !form.salAmt) {
      showToast("Fill employee name and amount", "error");
      return;
    }
    const entry = {
      id: Date.now(),
      employee: form.employee,
      amount: parseFloat(form.salAmt),
      date: form.salDate || TODAY,
      note: form.salNote || "",
    };
    try {
      const saved = await api.salaries.create(entry);
      save({ ...data, salaries: [saved, ...data.salaries] });
      setForm({});
      showToast("Salary paid and recorded!");
    } catch (err) {
      showToast(err.message, "error");
    }
  }

  async function del(type, id) {
    try {
      const apiMap = {
        purchases: api.purchases,
        expenses: api.expenses,
        sales: api.sales,
        salaries: api.salaries,
      };
      if (apiMap[type]) await apiMap[type].remove(id);
      save({ ...data, [type]: data[type].filter((e) => e.id !== id) });
      showToast("Entry removed");
    } catch (err) {
      showToast(err.message, "error");
    }
  }

  async function addDebt() {
    if (!form.debtVendor || !form.debtAmt) {
      showToast("Vendor and amount required", "error");
      return;
    }
    const total = parseFloat(form.debtAmt);
    const entry = {
      id: Date.now(),
      vendor: form.debtVendor,
      note: form.debtNote || "",
      totalAmount: total,
      remaining: total,
      interestRate: parseFloat(form.debtInt) || 0,
      dueDate: form.debtDue || "",
      addedDate: TODAY,
      payments: [],
    };
    try {
      const saved = await api.debts.create(entry);
      save({ ...data, debts: [saved, ...debts] });
      setForm({});
      showToast(`Debt of ${fmt(total)} added`);
    } catch (err) {
      showToast(err.message, "error");
    }
  }

  async function makePartialPayment(id) {
    const amt = parseFloat(partialAmt);
    if (!amt || amt <= 0) {
      showToast("Enter valid amount", "error");
      return;
    }
    try {
      const updated = await api.debts.recordPayment(id, amt);
      save({ ...data, debts: debts.map((d) => (d.id === id ? updated : d)) });
      setPartialPayId(null);
      setPartialAmt("");
      showToast(`Payment of ${fmt(amt)} recorded ✓`);
    } catch (err) {
      showToast(err.message, "error");
    }
  }

  async function clearDebt(id) {
    try {
      const updated = await api.debts.clear(id);
      save({ ...data, debts: debts.map((d) => (d.id === id ? updated : d)) });
      showToast("Debt fully cleared!");
    } catch (err) {
      showToast(err.message, "error");
    }
  }

  async function deleteDebt(id) {
    try {
      await api.debts.remove(id);
      save({ ...data, debts: debts.filter((d) => d.id !== id) });
      showToast("Debt removed");
    } catch (err) {
      showToast(err.message, "error");
    }
  }

  // ─── Export / Import ─────────────────────────────────────────────────────────
  function exportData() {
    const exportObj = {
      _appName: "ProfitLens",
      _version: SCHEMA_VERSION,
      _exportedAt: new Date().toISOString(),
      ...data,
    };
    const blob = new Blob([JSON.stringify(exportObj, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `profitlens-backup-${new Date().toISOString().split("T")[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showToast("Data exported successfully!");
  }

  function importData(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const raw = JSON.parse(e.target.result);
        const hasKnownField =
          raw.purchases || raw.sales || raw.expenses || raw.salaries || raw.debts;
        if (!hasKnownField) {
          setImportStatus({
            type: "error",
            msg: "Invalid file — does not look like a ProfitLens backup.",
          });
          return;
        }
        const parsed = parseIntoV2(raw);
        const merged = {
          ...data,
          purchases: mergeById(data.purchases, parsed.purchases),
          expenses: mergeById(data.expenses, parsed.expenses),
          sales: mergeById(data.sales, parsed.sales),
          salaries: mergeById(data.salaries, parsed.salaries),
          debts: mergeById(data.debts, parsed.debts),
          openingBalances: mergeOB(data.openingBalances, parsed.openingBalances),
          _version: SCHEMA_VERSION,
        };
        save(merged);
        const counts = [
          parsed.sales.length && `${parsed.sales.length} sales`,
          parsed.purchases.length && `${parsed.purchases.length} purchases`,
          parsed.expenses.length && `${parsed.expenses.length} expenses`,
          parsed.salaries.length && `${parsed.salaries.length} salaries`,
          parsed.debts.length && `${parsed.debts.length} debts`,
        ]
          .filter(Boolean)
          .join(", ");
        setImportStatus({
          type: "success",
          msg: `Imported: ${counts || "no records"}. Duplicates were skipped.`,
        });
        showToast("Data imported successfully!");
      } catch (err) {
        setImportStatus({
          type: "error",
          msg: `Failed to parse file: ${err.message}`,
        });
      }
    };
    reader.readAsText(file);
  }

  function replaceAllData(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const raw = JSON.parse(e.target.result);
        const hasKnownField =
          raw.purchases || raw.sales || raw.expenses || raw.salaries || raw.debts;
        if (!hasKnownField) {
          setImportStatus({
            type: "error",
            msg: "Invalid file — does not look like a ProfitLens backup.",
          });
          return;
        }
        const parsed = parseIntoV2(raw);
        save(parsed);
        setImportStatus({
          type: "success",
          msg: "All data replaced with imported file.",
        });
        showToast("Data replaced successfully!");
      } catch (err) {
        setImportStatus({
          type: "error",
          msg: `Failed to parse file: ${err.message}`,
        });
      }
    };
    reader.readAsText(file);
  }

  // ─── Ledger entries ───────────────────────────────────────────────────────────
  const ledgerEntries = useMemo(
    () =>
      [
        ...(data.openingBalances || []).map((b) => ({
          date: b.date,
          type: "Opening Balance",
          label: `Opening Balance${b.note ? ` — ${b.note}` : ""}`,
          detail: "",
          amount: b.amount,
          sign: 1,
        })),
        ...data.sales.map((s) => ({
          date: s.date,
          type: "Income",
          label: `Sale → ${s.client}`,
          detail: s.description,
          amount: s.amount,
          sign: 1,
        })),
        ...data.purchases.map((p) => ({
          date: p.payDue,
          type: "Vendor Credit",
          label: `Vendor: ${p.vendor}`,
          detail: `${p.item || "Purchase"} | Bought ${p.purchaseDate} | ${p.paid ? "✓ Paid" : "⏳ Pending"}`,
          amount: p.amount,
          sign: -1,
        })),
        ...(data.expenses || []).map((e) => ({
          date: e.date,
          type: "Expense",
          label: `${e.category}: ${e.description}`,
          detail: e.vendor ? `Paid to: ${e.vendor}` : "",
          amount: e.amount,
          sign: -1,
        })),
        ...data.salaries.map((s) => ({
          date: s.date,
          type: "Salary",
          label: `Salary → ${s.employee}`,
          detail: s.note,
          amount: s.amount,
          sign: -1,
        })),
        ...debts.flatMap((d) =>
          (d.payments || []).map((p) => ({
            date: p.date,
            type: "Debt Payment",
            label: `Debt → ${d.vendor}`,
            detail: p.fullClear ? "Full clearance" : "Partial payment",
            amount: p.amount,
            sign: -1,
          })),
        ),
      ]
        .sort((a, b) => b.date.localeCompare(a.date))
        .filter((e) => !filterDate || e.date === filterDate),
    [data, debts, filterDate],
  );

  const TYPE_COLORS = {
    "Opening Balance": { bg: "#001a2e", color: "#4fc3f7" },
    Income: { bg: "#003d2e", color: "#00c896" },
    "Vendor Credit": { bg: "#3d1f00", color: "#f5a623" },
    Expense: { bg: "#2d0a2d", color: "#e05ccc" },
    Salary: { bg: "#2a1f5e", color: "#8b7fe8" },
    "Debt Payment": { bg: "#1a1a2e", color: "#4fc3f7" },
  };

  const value = {
    // State
    data,
    authUser,
    authLoading,
    authMode,
    setAuthMode,
    authForm,
    setAuthForm,
    authError,
    setAuthError,
    authSubmitting,
    form,
    setForm,
    toast,
    filterDate,
    setFilterDate,
    debtStrategy,
    setDebtStrategy,
    availableCash,
    setAvailableCash,
    partialPayId,
    setPartialPayId,
    partialAmt,
    setPartialAmt,
    dailyViewDate,
    setDailyViewDate,
    importStatus,
    setImportStatus,
    // Handlers
    setOpeningBalance,
    addPurchase,
    markVendorPaid,
    addExpense,
    addSale,
    addSalary,
    del,
    addDebt,
    makePartialPayment,
    clearDebt,
    deleteDebt,
    exportData,
    importData,
    replaceAllData,
    handleAuthSubmit,
    handleLogout,
    save,
    showToast,
    // Computed values
    dailySummaries,
    todaySummary,
    viewedDaySummary,
    pendingVendor,
    debts,
    totalDebt,
    overdueDebts,
    debtResult,
    strategyList,
    expByCategory,
    totalExpenses,
    allTimeSalesTotal,
    allTimeVendorPaid,
    allTimeVendorUnpaid,
    allTimeSalaries,
    ledgerEntries,
    TYPE_COLORS,
    TODAY,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => useContext(AppContext);
